import type { ComponentType } from 'react';
import { House, BookOpenText, LandPlot, Cog, MessageSquareText, ShieldCheck, ShieldUser, ChartColumn } from 'lucide-react';
import { ANALYTICS_PATHS } from '@/constants/analytics-paths';
import { settingsNavGroups } from './settings-nav.config';
import type { RoleName } from '@/stores/permissions.store';
import { BACK_TESTING_PATH, DATASETS_PATH, PLAYGROUND_PATH, SIMULATOR_PATH, TEST_PREFIX } from '@/constants/paths';
import { ShadowAiIcon } from '@/components/icons/shadow-ai-icon';
import { AiAgentsIcon } from '@/components/icons/ai-agents-icon';
import { AiOperationsIcon } from '@/components/icons/ai-operations-icon';

export interface SubMenuItem {
	label: string;
	path: string;
	subject?: string;
	/** Shown only when workflow and graph editing is unlocked. */
	devOnly?: boolean;
	/** Shown only to super admins. */
	superAdminOnly?: boolean;
	/**
	 * Account feature flag that must be true for this entry to appear.
	 *
	 * A separate axis from `subject`: permissions say what a ROLE may see, this says what the
	 * ACCOUNT has switched on. A user can have every permission and still not have the feature.
	 */
	flag?: string;
}

export interface MenuItem {
	label: string;
	path: string;
	icon: ComponentType<{ className?: string }>;
	subItems?: SubMenuItem[];
	subject?: string;
	activePrefix?: string;
	/** Account feature flag that must be true for this entry to appear — see SubMenuItem.flag. */
	flag?: string;
	/** Roles that never see this entry, whatever their permissions grant. */
	hiddenForRoles?: RoleName[];
}

export const menuItems: MenuItem[] = [
	{
		label: 'Dashboard',
		path: '/',
		icon: House
	},
	// {
	// 	label: 'Analytics v2',
	// 	path: '/analytics-v2',
	// 	icon: BarChart3,
	// 	subject: 'analytics'
	// },
	{
		label: 'Training',
		path: '/training/protocols',
		icon: BookOpenText,
		activePrefix: '/training',
		subItems: [
			// Protocols replace Workflows here; old /training/workflows links redirect to them.
			{ label: 'Protocols', path: '/training/protocols', subject: 'workflow' },
			{ label: 'Sources', path: '/training/sources', subject: 'sources' },
			{ label: 'Instructions', path: '/training/instructions', subject: 'instructions' },
			// Hidden from the nav; the route still resolves for direct links.
			// { label: 'Behaviour', path: '/training/behaviour', subject: 'memory.brand' },
			{ label: 'Persona', path: '/training/persona', subject: 'persona' },
			{ label: 'Guardrails', path: '/training/guardrails', subject: 'guardrails' },
			{ label: 'Response Strategy', path: '/training/response-strategies', subject: 'response-strategy' },
			{ label: 'Promoted Protocols', path: '/training/promoted-workflows', subject: 'promoted-blueprint' },
			{ label: 'Promoted Knowledge', path: '/training/promoted-knowledge', subject: 'promoted-blueprint' },
			{ label: 'Products', path: '/training/products', subject: 'products' }
		]
	},
	{
		// Agents is its own destination rather than a Settings page: an agent is something the
		// brand runs, not something it configures once. Social Media and Sales so far.
		label: 'Agents',
		path: '/agents/social-media',
		icon: AiAgentsIcon,
		activePrefix: '/agents',
		subItems: [
			// The action log is reached from a button on the agent page, not from here — it reads
			// the same rules and is not a separate destination.
			{
				label: 'Social Media Agent',
				path: '/agents/social-media',
				subject: 'integrations'
			},
			{
				label: 'Sales Agent',
				path: '/agents/sales',
				subject: 'integrations'
			}
		]
	},
	{
		label: 'Sentinel Workspace',
		path: '/sentinel-workspace',
		icon: ShieldUser,
		activePrefix: '/sentinel-workspace',
		subject: 'sentinel-workspace'
	},
	{
		label: 'Analytics',
		path: ANALYTICS_PATHS.SUPPORT_OVERVIEW,
		icon: ChartColumn,
		activePrefix: ANALYTICS_PATHS.ROOT,
		subject: 'sentinel-workspace'
	},
	{
		label: 'Test',
		path: SIMULATOR_PATH,
		icon: LandPlot,
		activePrefix: TEST_PREFIX,
		subItems: [
			{ label: 'Simulator', path: SIMULATOR_PATH, subject: 'playground' },
			{ label: 'Playground', path: PLAYGROUND_PATH, subject: 'playground' },
			{ label: 'Back Testing', path: BACK_TESTING_PATH, subject: 'sop' },
			{ label: 'Datasets', path: DATASETS_PATH, subject: 'sop' }
		]
	},
	{
		label: 'Conversations',
		path: '/conversations',
		icon: ShadowAiIcon,
		subject: 'shadow-mode'
	},
	{
		label: 'Review Dashboard',
		path: '/sentinel-conversations',
		icon: ShieldCheck,
		subject: 'client-review'
	},
	{
		label: 'Client Feedback',
		path: '/client-feedbacks',
		icon: MessageSquareText,
		subject: 'client-feedback'
	},
	{
		label: 'Gap Report',
		path: '/gap-report',
		icon: AiOperationsIcon,
		activePrefix: '/gap-report',
		subject: 'gap-report'
	},
	{
		label: 'Settings',
		path: '/settings/users',
		icon: Cog,
		activePrefix: '/settings',
		// A Sentinel works the queue only; account and brand settings are not theirs to open.
		hiddenForRoles: ['sentinel'],
		// The settings sidebar's own pages, in its order, so the icon opens the first one it shows.
		subItems: settingsNavGroups.flatMap((group) => group.items)
	}
];
